import { useEffect, useMemo, useState } from "react";
import { usePlaylist, type PlaylistTrack } from "@/contexts/PlaylistContext";
import { ALBUM_DETAIL_ENDPOINT, albumDetailParams } from "@/hooks/useAlbumDetailQueue";
import { API_CACHE_TTL, fetchCachedGetApi } from "@/utils/apiCache";
import { fetchAppleMusicEnrichment } from "@/utils/appleMusicEnrichment";
import type { ArcadeSong } from "@/utils/arcade/lineup";
import { shouldIncludeSpotifyTrackForArtist } from "@/utils/spotifyArtistCredits";
import { trackIdentityKey } from "@/utils/trackIdentity";

interface AlbumTrack {
  id: string;
  name: string;
  preview_url?: string | null;
  artists?: Array<{ id?: string; name: string }>;
}

interface AlbumDetailResponse {
  album?: { id?: string; album_group?: string; tracks?: { items?: AlbumTrack[] } };
}

export type ArcadeTrackState = "loading" | "ready" | "unavailable";

export function sameSong(track: PlaylistTrack, song: ArcadeSong): boolean {
  if (song.trackId && track.id === song.trackId) return true;
  const wanted = trackIdentityKey(song.trackName || song.title);
  return Boolean(wanted) && trackIdentityKey(track.title || track.name) === wanted;
}

// Finds a playable preview for a song. A release card without a track name
// (an EP or album) plays its first track by this artist.
export async function resolveArcadeTrack(song: ArcadeSong, artistName: string): Promise<PlaylistTrack | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    return await resolveTrack(song, artistName, controller.signal);
  } finally {
    clearTimeout(timeout);
  }
}

async function resolveTrack(song: ArcadeSong, artistName: string, signal: AbortSignal): Promise<PlaylistTrack | null> {
  let trackName = song.trackName;
  let trackId = song.trackId;

  if (!trackName) {
    const data = await fetchCachedGetApi<AlbumDetailResponse>(
      ALBUM_DETAIL_ENDPOINT,
      albumDetailParams(artistName.toLowerCase(), { id: song.release.id, name: song.release.name }),
      { signal, ttlMs: API_CACHE_TTL.SIX_HOURS, shouldCache: (value) => Boolean(value?.album?.tracks?.items?.length) },
    );
    if (data?.album?.id && data.album.id !== song.release.id) return null;
    const items = data?.album?.tracks?.items || [];
    const first = items.find((item) => shouldIncludeSpotifyTrackForArtist({ album_group: song.release.albumGroup }, item, artistName)) ?? items[0];
    if (!first) return null;
    trackName = first.name;
    trackId = first.id;
    if (first.preview_url) {
      return { id: trackId, title: trackName, name: trackName, artist: artistName, artwork: song.artwork, audioUrl: first.preview_url, albumName: song.release.name };
    }
  }

  const enrichment = await fetchAppleMusicEnrichment(
    { artistName, releaseName: trackName, albumName: song.release.name, lookupType: "song", spotifyId: trackId },
    { signal, requirePreview: true, ttlMs: API_CACHE_TTL.SIX_HOURS },
  );
  if (!enrichment?.previewUrl) return null;
  return {
    id: trackId,
    title: trackName,
    name: trackName,
    artist: artistName,
    artwork: enrichment.artworkUrl || song.artwork,
    audioUrl: enrichment.previewUrl,
    albumName: song.release.name,
  };
}

export function useArcadeTrack(song: ArcadeSong | null, artistName: string): { track: PlaylistTrack | null; state: ArcadeTrackState } {
  const { playlist } = usePlaylist();
  const queued = useMemo(() => (song ? playlist.find((track) => track.audioUrl && sameSong(track, song)) ?? null : null), [playlist, song]);
  const [resolved, setResolved] = useState<{ slug: string; track: PlaylistTrack | null } | null>(null);
  const slug = song?.slug;

  useEffect(() => {
    if (!song || queued) return;
    let cancelled = false;
    resolveArcadeTrack(song, artistName)
      .catch(() => null)
      .then((track) => {
        if (!cancelled) setResolved({ slug: song.slug, track });
      });
    return () => {
      cancelled = true;
    };
    // The song object is rebuilt with the lineup; its slug is the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, artistName, Boolean(queued)]);

  if (queued) return { track: queued, state: "ready" };
  if (resolved && resolved.slug === slug) return { track: resolved.track, state: resolved.track ? "ready" : "unavailable" };
  return { track: null, state: "loading" };
}
